import { NetworkStatus, useQuery } from "@apollo/client";
import { Box } from "@mui/material";
import { useFormikContext } from "formik";
import { uniq } from "lodash";
import { ReactElement, useState } from "react";

import { isUUID } from "@stw/uuid";

import {
  CampaignContactFollowUpImportArgs,
  CampaignFilterInput,
  CampaignListResult,
  CampaignSortBy,
  CampaignStatus,
  ListCampaignsQuery,
  ListCampaignsQueryVariables,
} from "~frontend/__generated__/graphql";
import Loading from "~frontend/components/Loading";
import { LIST_CAMPAIGNS } from "~frontend/graphql/campaigns";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { CAMPAIGN_SORTS } from "~frontend/routes/subaccounts/campaignsList/constants";

import FollowUpCampaignSelectorControls from "./FollowUpCampaignSelectorControls";
import FollowUpCampaignSelectorTable from "./FollowUpCampaignSelectorTable";

export default function FollowUpCampaignSelector({
  selectedCampaigns,
  setSelectedCampaigns,
  readOnly,
  currentCampaignId,
  footerActions,
  campaignIntegrationFilters,
}: {
  selectedCampaigns: CampaignListResult[];
  setSelectedCampaigns: (newVal: CampaignListResult[]) => void;
  readOnly?: boolean;
  currentCampaignId: string;
  footerActions: React.ReactNode;
  campaignIntegrationFilters: Record<string, any>;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const selectedCampaignIds = selectedCampaigns.map(c => c.id);

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [statusFilter, setStatusFilter] = useState(
    Object.values(CampaignStatus)
  );
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<CampaignSortBy>(CAMPAIGN_SORTS[0].value);

  const filter: CampaignFilterInput = {
    statusIn: statusFilter,
    excludeUnstarted: true,
  };

  if (search) {
    if (isUUID(search)) {
      filter.idIn = [search.trim().toLowerCase()];
    } else {
      filter.nameContains = search;
    }
  }

  const selectableCampaignResults = useQuery<
    ListCampaignsQuery,
    ListCampaignsQueryVariables
  >(LIST_CAMPAIGNS, {
    variables: {
      input: {
        limit: pageSize,
        offset: page * pageSize,
        filter,
        excludedCampaignIds: [currentCampaignId],
        sort,
        subaccountId: subaccount.id,
        ...campaignIntegrationFilters,
      },
    },
    fetchPolicy: "network-only",
  });

  const formik = useFormikContext<CampaignContactFollowUpImportArgs>();

  const initialLoading =
    selectableCampaignResults.networkStatus === NetworkStatus.loading;

  const selectableCampaignResultsData =
    selectableCampaignResults.networkStatus === NetworkStatus.setVariables
      ? selectableCampaignResults.previousData
      : selectableCampaignResults.data;

  return (
    <Box
      sx={{
        "& .MuiTableCell-footer .MuiTablePagination-spacer": {
          flex: "none",
        },
      }}
    >
      <FollowUpCampaignSelectorControls
        statusFilter={statusFilter}
        setStatusFilter={filter => {
          setStatusFilter(filter);
          setPage(0);
        }}
        search={search}
        setSearch={search => {
          setSearch(search);
          setPage(0);
        }}
        sort={sort}
        setSort={sort => {
          setSort(sort);
          setPage(0);
        }}
      />
      {initialLoading || !selectableCampaignResultsData?.listCampaigns ? (
        <Loading />
      ) : (
        <FollowUpCampaignSelectorTable
          rows={(
            selectableCampaignResultsData!.listCampaigns
              .campaigns as CampaignListResult[]
          ).filter(
            campaign1 =>
              !selectedCampaigns.some(
                campaign2 => campaign1.id === campaign2.id
              )
          )}
          totalCount={selectableCampaignResultsData!.listCampaigns.totalCount}
          page={page}
          setPage={setPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          selectedCampaignIds={selectedCampaignIds}
          selectedCampaigns={selectedCampaigns}
          setSelectedCampaigns={async selectedRows => {
            const selectableCampaignData = (selectableCampaignResultsData
              ?.listCampaigns.campaigns || []) as CampaignListResult[];

            const newSelectedCampaigns = uniq([
              ...selectedCampaigns,
              ...selectableCampaignData,
            ]).filter(campaign => {
              return selectedRows.includes(campaign.id);
            }) as CampaignListResult[];

            const removedCampaigns = selectedCampaigns.filter(
              campaign => !selectedRows.includes(campaign.id)
            );
            const removedCampaignScriptVariantIds = new Set(
              removedCampaigns
                .flatMap(campaign => campaign.scriptVariants)
                .map(scriptVariant => scriptVariant.id)
            );
            await formik.setFieldValue(
              "followUpScriptVariantIds",
              formik.values.followUpScriptVariantIds?.filter(
                (id: string) => !removedCampaignScriptVariantIds.has(id)
              )
            );

            setSelectedCampaigns(newSelectedCampaigns);
            await formik.setFieldValue("followUpCampaignIds", selectedRows);
          }}
          readOnly={readOnly}
          footerActions={footerActions}
        />
      )}
    </Box>
  );
}
